// Раздел «Карточки»: своя схема — та, в которой мы останемся после отключения Bitrix.
//
// НА ЭКРАНЕ ДВЕ РАЗНЫЕ ВЕЩИ, И ОНИ РАЗДЕЛЕНЫ НАМЕРЕННО.
//
// Сверху — ЖИВОЙ ответ ядра (`/cards/refs`): какие таблицы своей схемы заведены и сколько
// в них строк. Это правда о состоянии переноса, а не рассказ о нём. «Нет таблицы» и «ноль
// строк» показаны по-разному: первое значит, что схема ещё не дописана, второе — что
// дописана, а данные не переносились. Одинаковый вид у этих двух ответов однажды заставил
// бы искать данные там, где их некуда положить.
//
// Снизу — ЖИВЫЕ карточки своей схемы: найти запись поиском (тем же методом, что у
// полей-связей), открыть, поправить, завести новую. Перенос наполнил таблицы, владелец
// 29.07.2026 разрешил запись в свою схему — подделка больше не нужна. Включена ли запись
// на сервере, экран не угадывает: он показывает ответ /cards/refs словами ядра.
import { useEffect, useState } from 'react'

import { useMe } from '../api/hooks'
import { CardScreen, ОПИСАНИЯ, источникКарточек } from '../cards'
import type { Сущность, Ярлык } from '../cards'
import { сведенияОКарточках, type СведенияОКарточках } from '../cards/source_bff'
import { QueryState } from '../components/QueryState'
import { plural } from '../lib/format'

function СостояниеСхемы() {
  const [данные, setДанные] = useState<СведенияОКарточках | null>(null)
  const [беда, setБеда] = useState<Error | null>(null)
  const [ждём, setЖдём] = useState(true)

  useEffect(() => {
    let жив = true
    сведенияОКарточках()
      .then((д) => { if (жив) setДанные(д) })
      .catch((e: unknown) => { if (жив) setБеда(e instanceof Error ? e : new Error(String(e))) })
      .finally(() => { if (жив) setЖдём(false) })
    return () => { жив = false }
  }, [])

  const заведено = данные ? данные.сущности.filter((с) => с.есть).length : 0
  const всего = данные ? данные.сущности.length : 0

  return (
    <QueryState isLoading={ждём} error={беда}>
      {!данные ? null : (
        <div className="card">
          <div className="hint">
            Заведено <span className="num">{заведено}</span> из{' '}
            <span className="num">{всего}</span>{' '}
            {plural(всего, 'таблицы', 'таблиц', 'таблиц')} своей схемы. «Нет таблицы» и
            «ноль строк» — разные ответы: первое значит, что схема ещё не дописана, второе
            — что данные в неё не переносились.
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Карточка</th>
                  <th>Таблица</th>
                  <th>Строк</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {данные.сущности.map((с) => (
                  <tr key={с.код}>
                    <td data-label="Карточка">{с.код}</td>
                    <td data-label="Таблица">{с.таблица}</td>
                    <td data-label="Строк">
                      {с.строк === null ? '—' : <span className="num">{с.строк}</span>}
                    </td>
                    <td data-label="Состояние">
                      {с.есть ? 'таблица заведена' : (с.почему ?? 'таблицы в схеме ещё нет')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint">
            {данные.записьВключена
              ? 'Запись включена'
              : `Запись выключена: ${данные.почемуНетЗаписи}`}
          </div>
        </div>
      )}
    </QueryState>
  )
}

export default function Cards() {
  const me = useMe()

  return (
    <>
      <div className="page-head">
        <h1>Карточки</h1>
        <div className="hint">
          Те же компании, суда и люди, но из своей базы, а не из зеркала Bitrix. Сверху —
          что в своей схеме заведено на самом деле; снизу — живые карточки: найти,
          поправить, завести новую
        </div>
      </div>
      <QueryState isLoading={me.isLoading} error={me.error}>
        {!me.data ? (
          <div className="card empty">
            <div className="empty-title">Учётка не определена</div>
            <div className="empty-hint">
              Кто вы — решает BFF, а право правки проверяется по учётке. Раз ответа нет,
              карточку не показываем: это отказ, а не «записей нет».
            </div>
          </div>
        ) : (
          <>
            <СостояниеСхемы />
            <ЖивыеКарточки />
          </>
        )}
      </QueryState>
    </>
  )
}


const ПОРЯДОК: Сущность[] = [
  'сделка', 'компания', 'контакт', 'сотрудник', 'судно', 'договор', 'счёт', 'акт', 'проверка',
]

function ЖивыеКарточки() {
  const [сущность, setСущность] = useState<Сущность>('компания')
  const [запрос, setЗапрос] = useState('')
  const [найдено, setНайдено] = useState<Ярлык[] | null>(null)
  const [ищем, setИщем] = useState(false)
  const [ошибкаПоиска, setОшибкаПоиска] = useState<string | null>(null)
  const [открыт, setОткрыт] = useState<string | null>(null)

  async function искать() {
    const щ = запрос.trim()
    if (!щ) { setНайдено(null); return }
    setИщем(true)
    setОшибкаПоиска(null)
    try {
      setНайдено(await источникКарточек.поиск(сущность, щ))
    } catch (e: unknown) {
      // «Ничего не нашли» и «поиск не ответил» ведут человека в разные стороны —
      // не подменяем одно другим.
      setНайдено(null)
      setОшибкаПоиска(e instanceof Error ? e.message : String(e))
    } finally {
      setИщем(false)
    }
  }

  const оп = ОПИСАНИЯ[сущность]

  return (
    <>
      <div className="card">
        <div className="card-demo-pick">
          <div className="f">
            <label htmlFor="cards-entity">Сущность</label>
            <select
              id="cards-entity"
              value={сущность}
              onChange={(е) => {
                setСущность(е.target.value as Сущность)
                setНайдено(null)
                setОткрыт(null)
              }}
            >
              {ПОРЯДОК.filter((с) => ОПИСАНИЯ[с]).map((с) => (
                <option key={с} value={с}>{ОПИСАНИЯ[с].много}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="cards-q">Найти</label>
            <input
              id="cards-q"
              value={запрос}
              placeholder="название, номер, ИНН…"
              onChange={(е) => setЗапрос(е.target.value)}
              onKeyDown={(е) => { if (е.key === 'Enter') void искать() }}
            />
          </div>
          <div className="f">
            <label>&nbsp;</label>
            <button type="button" onClick={() => void искать()} disabled={ищем}>
              {ищем ? 'Ищем…' : 'Искать'}
            </button>
          </div>
          <div className="f">
            <label>&nbsp;</label>
            <button type="button" onClick={() => setОткрыт('new')}>Новая карточка</button>
          </div>
        </div>
        {ошибкаПоиска ? <div className="hint">Поиск не ответил: {ошибкаПоиска}</div> : null}
        {найдено && найдено.length === 0 ? (
          <div className="hint">По запросу ничего нет среди {оп.много.toLowerCase()} своей базы</div>
        ) : null}
        {найдено && найдено.length > 0 ? (
          <ul>
            {найдено.map((я) => (
              <li key={я.id}>
                <button type="button" onClick={() => setОткрыт(String(я.id))}>
                  {я.название || `№ ${я.id}`}
                </button>
                {я.подсказка ? <span className="hint"> {я.подсказка}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {открыт ? (
        <CardScreen
          key={`${сущность}:${открыт}`}
          сущность={сущность}
          id={открыт}
          источник={источникКарточек}
        />
      ) : (
        <div className="card empty">
          <div className="empty-title">Карточка не открыта</div>
          <div className="empty-hint">
            Найдите запись поиском выше или заведите новую — форма откроется здесь
          </div>
        </div>
      )}
    </>
  )
}
